import React, { useEffect, useState, useMemo } from "react";
import {
  useAssessment,
  useAssessmentConfiguration,
  useYearLevelByEducationLevel,
} from "../../../../hooks/useAssessment";
import Button from "../../../shared/Button";
import CustomModal from "../../../shared/CustomModal";
import AssessmentConfigurationModal from "./AssessmentConfigurationModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import SubjectConfigurationForm from "./SubjectConfigurationForm";
import { Spinner } from "reactstrap";
import { convertSeconds, extractTimeFromDate } from "../../../../utils";
import swal from "sweetalert";
import Select from "../../../shared/Select";
import { useARRCode } from "../../../../context/ARRLevelContext";
import AssessmentDetailForm from "./AssessmentDetailForm";
import CopyClassroomConfigModal from "./CopyClassroomConfigModal";

const AssessmentForm = ({
  copiedId,
  isFetching,
  assessmentConfiguration,
  setAssessmentConfiguration,
  refetchPreviousAssessment,
  type,
  data,
  closeModal,
  handleAssessmentUpdate,
  updating,
}) => {
  const { arrLevel } = useARRCode();
  const { assessments, refetchAssessment } = useAssessment();
  const { loading, createAssessmentConfiguration } =
    useAssessmentConfiguration();
  const { yearLevel, setYearLevel, getYearLevelByEducationLevel } =
    useYearLevelByEducationLevel();
  const [formData, setFormData] = useState({
    assessmentTypeCode: "",
    educationLevelId: "",
    educationLevelDivisionId: "",
    startTime: "",
    hour: "",
    minutes: "",
  });
  const [assessmentName, setAssessmentName] = useState("");
  const [isRecorded, setIsRecorded] = useState(false);
  const [assessmentSubjectConfigurations, setAssessmentSubjectConfigurations] =
    useState([]);
  const [assessmentGeneralConfigurations, setAssessmentGeneralConfigurations] =
    useState([]);
  const [aggregationWeightConfiguration, setAggregationWeightConfiguration] =
    useState([{ assessment: null, weight: "" }]);
  const [yearLevelId, setYearLevelId] = useState(null);
  const [recordingDateCheckbox, setRecordingDateCheckbox] = useState(true);
  const [recordingDate, setRecordingDate] = useState({
    date: "1",
    type: "weeks",
  });
  const [activeTab, setActiveTab] = useState(null);
  const [accordion, setAccordion] = useState(null);
  const [copyConfigModal, setCopyConfigModal] = useState(null);
  const [copyConfig, setCopyConfig] = useState(null);

  const isEdit = type === "edit";

  const yearLevelConfig = useMemo(() => {
    if (yearLevel?.length === 0) return {};

    const map = {};
    yearLevel.forEach((item) => {
      map[item.id] = item;
    });

    return map;
  }, [yearLevel]);

  const classroomOptions = useMemo(() => {
    if (Object.keys(yearLevelConfig).length === 0) return [];

    const clasrooms = yearLevelConfig[activeTab]?.classrooms;
    return clasrooms?.map((item) => ({
      label: item?.name,
      value: item?.id,
    }));
  }, [activeTab, yearLevelConfig]);

  //validate form Data
  const validateFormData = () => {
    if (!assessmentName.trim()) {
      swal("Oops", "Assessment Name is requried");
      return false;
    }

    const regex = /^[\s_-]|[\s_-]$/;
    if (regex.test(assessmentName.trim())) {
      swal(
        "Oops",
        "Assessment Name cannot starts or ends with special characters",
      );
      return false;
    }

    if (!formData.educationLevelDivisionId) {
      swal("Oops", "Term is requried");
      return false;
    }
    if (!formData.assessmentTypeCode) {
      swal("Oops", "Assessment Type is requried");
      return false;
    }

    return true;
  };

  //validate subject configuration
  const validSubjectConfigurationData = (data) => {
    const isFilled = (val) => val !== null && val !== undefined && val !== "";

    let errors;

    const isTheoryFilled = (item) => {
      if (!isFilled(item?.assessmentTheoryFromDate)) {
        errors = {
          item: item,
          message: "Assessment Date is missing for theory subject",
        };
        return false;
      }
      if (!isFilled(item?.assessmentTheoryStartTime)) {
        errors = {
          item: item,
          message: "Assessment Start Time is missing for theory subject",
        };
        return false;
      }
      if (
        !isFilled(item?.assessmentTheoryHour) ||
        !isFilled(item?.assessmentTheoryMinutes)
      ) {
        errors = {
          item: item,
          message: "Assessment Duration is missing for theory subject",
        };
        return false;
      }

      return true;
    };

    const isPracticalFilled = (item) => {
      if (!isFilled(item?.assessmentPracticalFromDate)) {
        errors = {
          item: item,
          message: "Assessment Date is missing for practical subject",
        };
        return false;
      }
      if (!isFilled(item?.assessmentPracticalStartTime)) {
        errors = {
          item: item,
          message: "Assessment Start Time is missing for practical subject",
        };
        return false;
      }
      if (
        !isFilled(item?.assessmentPracticalHour) ||
        !isFilled(item?.assessmentPracticalMinutes)
      ) {
        errors = {
          item: item,
          message: "Assessment Duration is missing for practical subject",
        };
        return false;
      }
      return true;
    };

    const isRowValid = data.every((item) => {
      if (type === "edit" && !item?.hasTheory && !item?.hasPractical) {
        return true;
      }

      if (type === "create" && !item?.hasTheory && !item?.hasPractical) {
        errors = {
          item: item,
          message: "Please fill all required details",
        };
        return false;
      }

      if (item?.hasTheory && item?.hasPractical) {
        return isTheoryFilled(item) && isPracticalFilled(item);
      }

      if (item?.hasTheory && !item?.hasPractical) {
        return isTheoryFilled(item);
      }

      if (!item?.hasTheory && item?.hasPractical) {
        return isPracticalFilled(item);
      }

      return true;
    });

    const yearLevel = yearLevelConfig[errors?.item?.yearLevelId];
    const errorYearLevelName = yearLevel?.name;
    const errorClassroomName = yearLevel?.classrooms?.find(
      (item) => item?.id === errors?.item?.classroomId,
    )?.name;

    if (!isRowValid) {
      swal(
        "Oops",
        errors
          ? `${errors?.message} in Class ${errorYearLevelName} - ${errorClassroomName}`
          : "Please fill all the required details",
      );
      return null;
    }

    return data
      .map((item) => {
        const theoryFilled = isTheoryFilled(item);
        const practicalFilled = isPracticalFilled(item);

        if (!theoryFilled && !practicalFilled) return null;

        return {
          yearLevelId: item?.yearLevelId,
          classroomId: item?.classroomId,
          subjectId: item?.subjectId,
          educationLevelId: formData?.educationLevelId,
          educationLevelDivisionId: formData?.educationLevelDivisionId,

          ...(theoryFilled && {
            assessmentTheoryFromDate: `${item.assessmentTheoryFromDate} ${item.assessmentTheoryStartTime}`,
            assessmentTheoryDuration: `PT${item.assessmentTheoryHour}H${item.assessmentTheoryMinutes}M`,
            recordingTheoryDate: item?.recordingTheoryDate,
            recordingTheoryDeadlineDate: item?.recordingTheoryDeadlineDate,
          }),

          ...(practicalFilled && {
            assessmentPracticalFromDate: `${item.assessmentPracticalFromDate} ${item.assessmentPracticalStartTime}`,
            assessmentPracticalDuration: `PT${item.assessmentPracticalHour}H${item.assessmentPracticalMinutes}M`,
            recordingPracticalDate: item?.recordingPracticalDate,
            recordingPracticalDeadlineDate:
              item?.recordingPracticalDeadlineDate,
          }),
        };
      })
      .filter(Boolean);
  };

  //validate generalConfiguration
  const validGeneralConfigurationData = (data) => {
    const isValid = data?.every(
      (item) => item?.assessmentFromDate && item?.assessmentToDate,
    );

    if (!isValid) {
      swal("Oops", "Please fill all the required fields in general assessment");
      return null;
    }

    return data;
  };

  //submit form
  const handleSubmit = async () => {
    if (!validateFormData()) return;

    const generalConfiguration = assessmentGeneralConfigurations?.filter(
      (item) => item?.checked,
    );
    const subjectConfiguration = assessmentSubjectConfigurations?.filter(
      (item) => item?.checked,
    );

    if (
      generalConfiguration?.length === 0 &&
      subjectConfiguration?.length === 0
    ) {
      swal("Oops", "At least subject or general assessment must be filled");
      return;
    }

    const subjectConfig =
      subjectConfiguration?.length > 0
        ? validSubjectConfigurationData(subjectConfiguration)
        : null;

    const generalConfig =
      generalConfiguration?.length > 0
        ? validGeneralConfigurationData(generalConfiguration)
        : null;

    if (
      (subjectConfiguration.length > 0 && !subjectConfig) ||
      (generalConfiguration.length > 0 && !generalConfig)
    ) {
      return;
    }

    const weightConfiguration = (aggregationWeightConfiguration || [])
      .filter((item) => item?.assessment && item?.weight !== "")
      .map((item) => ({
        sourceAssessment: item.assessment,
        percentageWeight: item.weight,
      }));

    const params = {
      copyFrom: copiedId,
      aggregationWeightConfiguration: weightConfiguration,
      assessmentGeneralConfigurations: generalConfig ?? [],
      assessmentName: assessmentName,
      assessmentSubjectConfigurations: subjectConfig ?? [],
      assessmentTypeCode: formData.assessmentTypeCode,
      educationLevelDivisionId: formData.educationLevelDivisionId,
      educationLevelId: formData.educationLevelId,
    };

    if (isEdit) {
      const updatedData = { ...params, id: data.id };
      handleAssessmentUpdate(updatedData);
    } else {
      try {
        await createAssessmentConfiguration(params);
        refetchAssessment();
        refetchPreviousAssessment();
        setFormData((prev) => ({
          ...prev,
          assessmentTypeCode: "",
          educationLevelDivisionId: "",
          startTime: "",
          hour: "",
          minutes: "",
        }));
        setAssessmentName("");
        setYearLevelId(null);
        setAggregationWeightConfiguration([]);
        setAssessmentSubjectConfigurations([]);
        setAssessmentGeneralConfigurations([]);
      } catch (error) {
        console.error("Create Failed", error);
      }
      return;
    }
  };

  //reset assessment configuration of one section
  const resetAssessmentConfigurationForClassroom = (
    yearLevelId,
    classroomId,
  ) => {
    setAssessmentSubjectConfigurations((prev) =>
      prev?.map((item) => {
        if (
          item.yearLevelId !== yearLevelId ||
          item.classroomId !== classroomId
        ) {
          return item;
        }

        return {
          ...item,
          checked: false,

          ...(item?.hasTheory && {
            assessmentTheoryFromDate: "",
            assessmentTheoryStartTime: "",
            assessmentTheoryHour: "",
            assessmentTheoryMinutes: "",
            recordingTheoryDate: "",
            recordingTheoryDeadlineDate: "",
          }),

          ...(item?.hasPractical && {
            assessmentPracticalFromDate: "",
            assessmentPracticalStartTime: "",
            assessmentPracticalHour: "",
            assessmentPracticalMinutes: "",
            recordingPracticalDate: "",
            recordingPracticalDeadlineDate: "",
          }),
        };
      }),
    );

    setAssessmentGeneralConfigurations((prev) =>
      prev?.filter(
        (item) =>
          !(
            item?.yearLevelId === yearLevelId &&
            item?.classroomId === classroomId
          ),
      ),
    );
  };

  //populate assessment data
  const populateAssessment = (assessment) => {
    if (type === "edit") {
      setAssessmentName(assessment?.assessmentName);
    }

    setFormData((prev) => ({
      ...prev,
      assessmentTypeCode: assessment?.assessmentTypeCode,
      educationLevelId: assessment?.educationLevelId,
      educationLevelDivisionId: Number(assessment?.educationLevelDivisionId),
    }));

    const subjectYearLevelId =
      assessment?.assessmentSubjectConfigurations?.map(
        (item) => item.yearLevelId,
      ) ?? [];

    const generalYearLevelId =
      assessment?.assessmentGeneralConfigurations?.map(
        (item) => item.yearLevelId,
      ) ?? [];

    const yearLevelId = [...subjectYearLevelId, ...generalYearLevelId];

    //extract unique year level
    const uniqueYearLevelId = yearLevelId.reduce((acc, value) => {
      if (!acc.includes(value)) {
        acc.push(value);
      }
      return acc;
    }, []);

    if (!yearLevel?.length) return;

    const mappedYearLevels = uniqueYearLevelId
      .map((id) => {
        const found = yearLevel?.find((y) => y.id === id);
        return found ? { label: found.name, value: found.id } : null;
      })
      .filter(Boolean);

    setYearLevelId(mappedYearLevels);
    setFormData((prev) => ({ ...prev, yearLevelId: mappedYearLevels }));

    const aggregationConfiguration =
      assessment?.aggregationWeightConfiguration?.length > 0
        ? assessment.aggregationWeightConfiguration.map((item) => ({
            assessment: item?.sourceAssessment,
            weight: item?.percentageWeight,
          }))
        : [{ assessment: null, weight: "" }];

    setAggregationWeightConfiguration(aggregationConfiguration);

    const processedSubjectConfiguration =
      assessment?.assessmentSubjectConfigurations?.map((item) => {
        return {
          yearLevelId: item?.yearLevelId,
          classroomId: item?.classroomId,
          subjectId: item?.subjectId,
          checked: true,
          assessmentTheoryStartTime:
            item?.assessmentTheoryFromDate?.split(" ")[1],
          assessmentTheoryHour: item?.assessmentTheoryDuration
            ? convertSeconds(item?.assessmentTheoryDuration)?.hours
            : "",
          assessmentTheoryMinutes: item?.assessmentTheoryDuration
            ? convertSeconds(item?.assessmentTheoryDuration)?.minutes
            : "",
          assessmentPracticalStartTime:
            item?.assessmentPracticalFromDate?.split(" ")[1],
          assessmentPracticalHour: item?.assessmentPracticalDuration
            ? convertSeconds(item?.assessmentPracticalDuration)?.hours
            : "",
          assessmentPracticalMinutes: item?.assessmentPracticalDuration
            ? convertSeconds(item?.assessmentPracticalDuration)?.minutes
            : "",
          ...(type === "edit" && {
            isRecorded: item?.isRecorded,
            assessmentTheoryFromDate:
              item?.assessmentTheoryFromDate?.split(" ")[0],
            recordingTheoryDate: item?.recordingTheoryDate,
            recordingTheoryDeadlineDate: item?.recordingTheoryDeadlineDate,
            assessmentPracticalFromDate:
              item?.assessmentPracticalFromDate?.split(" ")[0],
            recordingPracticalDate: item?.recordingPracticalDate,
            recordingPracticalDeadlineDate:
              item?.recordingPracticalDeadlineDate,
          }),
        };
      });

    setAssessmentSubjectConfigurations(processedSubjectConfiguration);

    if (assessment?.assessmentGeneralConfigurations?.length > 0) {
      setAssessmentGeneralConfigurations(() => {
        return assessment?.assessmentGeneralConfigurations?.map((item) => ({
          ...item,
          isRecorded: type === "edit" ? item?.isRecorded : false,
          checked: true,
        }));
      });
    }

    if (!isEdit) {
      setAssessmentConfiguration(null);
    }

    return;
  };

  //set Active tab for accordion
  useEffect(() => {
    if (yearLevelId?.length) {
      setActiveTab(yearLevelId[0].value);
    }
  }, [yearLevelId]);

  //populate update data
  useEffect(() => {
    if (type === "create" && !assessmentConfiguration) return;
    if (type === "edit" && !data) return;

    if (data && isEdit) {
      populateAssessment(data);
      return;
    }

    if (assessmentConfiguration && !isEdit) {
      populateAssessment(assessmentConfiguration);
      return;
    }
  }, [data, isEdit, assessmentConfiguration, yearLevel]);

  useEffect(() => {
    setFormData({
      assessmentTypeCode: "",
      educationLevelId: "",
      educationLevelDivisionId: "",
      startTime: "",
      hour: "",
      minutes: "",
    });
    setYearLevelId(null);
    setYearLevel([]);
  }, [arrLevel]);

  return (
    <>
      {isFetching ? (
        <div className="arr-spinner">
          <div className="loader">
            <Spinner size={"md"} color="primary"></Spinner>
          </div>
          <div style={{ fontSize: "13px", marginTop: "15px" }}>
            Please wait !! Processing.....
          </div>
        </div>
      ) : (
        <>
          <AssessmentDetailForm
            data={data}
            isEdit={isEdit}
            formData={formData}
            assessmentName={assessmentName}
            yearLevelId={yearLevelId}
            yearLevel={yearLevel}
            assessments={assessments}
            aggregationWeightConfiguration={aggregationWeightConfiguration}
            recordingDateCheckbox={recordingDateCheckbox}
            recordingDate={recordingDate}
            setFormData={setFormData}
            setAssessmentName={setAssessmentName}
            setYearLevelId={setYearLevelId}
            setRecordingDateCheckbox={setRecordingDateCheckbox}
            setRecordingDate={setRecordingDate}
            setAggregationWeightConfiguration={
              setAggregationWeightConfiguration
            }
            setAssessmentSubjectConfigurations={
              setAssessmentSubjectConfigurations
            }
            setAssessmentGeneralConfigurations={
              setAssessmentGeneralConfigurations
            }
            getYearLevelByEducationLevel={getYearLevelByEducationLevel}
          />

          {type === "edit" &&
            data?.aggregationWeightConfiguration?.length > 0 && (
              <div className="arr-note">
                <strong>Note: </strong>This assessment includes an aggregation
                weight configuration and can be edited as needed.
              </div>
            )}

          <div className="arr-tabs" style={{ marginTop: "32px" }}>
            {yearLevelId?.map((item) => (
              <Button
                key={item.value}
                onClick={() => setActiveTab(item.value)}
                variant={activeTab === item.value ? "primary" : "outline"}
              >
                Class {item.label}
              </Button>
            ))}
          </div>

          {yearLevelId?.length > 0 &&
            activeTab &&
            yearLevel?.flatMap((item) => {
              if (item.id === activeTab) {
                return item?.classrooms?.length > 0 ? (
                  item?.classrooms?.map((classroom) => {
                    return (
                      <div
                        key={classroom.id}
                        style={{ marginTop: "16px", position: "relative" }}
                        className="arr-accordion"
                      >
                        <div
                          className="flex"
                          style={{
                            justifyContent: "space-between",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            if (accordion === classroom.id) {
                              setAccordion(null);
                              return;
                            }
                            setAccordion(classroom.id);
                          }}
                        >
                          <p style={{ margin: 0 }}>{classroom?.name}</p>

                          <FontAwesomeIcon fontSize={18} icon={faChevronDown} />
                        </div>

                        <div
                          className="flex"
                          style={{
                            position: "absolute",
                            top: "9px",
                            right: "55px",
                            gap: "16px",
                          }}
                        >
                          <Button
                            onClick={() => {
                              setCopyConfigModal(true);
                              setCopyConfig({
                                fromClassroomId: classroom.id,
                                yearLevelId: activeTab,
                              });
                            }}
                            size="sm"
                            variant="outline"
                          >
                            Copy Routine
                          </Button>
                          <Button
                            onClick={() =>
                              resetAssessmentConfigurationForClassroom(
                                activeTab,
                                classroom.id,
                              )
                            }
                            disabled={isRecorded}
                            size="sm"
                            variant="danger"
                          >
                            Reset
                          </Button>
                        </div>

                        <div>
                          {accordion === classroom.id && (
                            <SubjectConfigurationForm
                              yearLevelId={activeTab}
                              classroomId={classroom.id}
                              subjects={classroom?.subjects}
                              assessmentSubjectConfigurations={
                                assessmentSubjectConfigurations
                              }
                              setAssessmentSubjectConfigurations={
                                setAssessmentSubjectConfigurations
                              }
                              assessmentGeneralConfigurations={
                                assessmentGeneralConfigurations
                              }
                              setAssessmentGeneralConfigurations={
                                setAssessmentGeneralConfigurations
                              }
                              recordingDateCheckbox={recordingDateCheckbox}
                              recordingDate={recordingDate}
                              defaultStartTime={formData?.startTime}
                              defaultHour={formData?.hour}
                              defaultMinutes={formData?.minutes}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div
                    className="accordion"
                    style={{ textAlign: "center", marginTop: "16px" }}
                  >
                    No Section Found
                  </div>
                );
              }
              return [];
            })}

          <div style={{ marginTop: "32px" }} className="action-btns end">
            {isEdit && (
              <Button variant="outline" onClick={closeModal}>
                Cancel
              </Button>
            )}
            <Button
              disabled={loading.create || updating}
              onClick={handleSubmit}
            >
              {loading.create || updating ? (
                <Spinner size={"sm"} color="white" />
              ) : isEdit ? (
                "Update Assessment"
              ) : (
                "Create Assessment"
              )}
            </Button>
          </div>
        </>
      )}

      <CopyClassroomConfigModal
        classroomOptions={classroomOptions}
        copyConfigModal={copyConfigModal}
        yearLevelConfig={yearLevelConfig}
        copyConfig={copyConfig}
        setCopyConfigModal={setCopyConfigModal}
        assessmentSubjectConfigurations={assessmentSubjectConfigurations}
        assessmentGeneralConfigurations={assessmentGeneralConfigurations}
        setAssessmentSubjectConfigurations={setAssessmentSubjectConfigurations}
        setAssessmentGeneralConfigurations={setAssessmentGeneralConfigurations}
      />
    </>
  );
};

export default AssessmentForm;
